import {
  ICheckoutIntentRequest,
  ICheckoutIntentResponse,
  ICheckoutOrder,
  IGetPartnerRafflesParams,
  IPrizeDrawPurchaseEntry,
  IPrizeDrawPurchaseResponse,
  IRaffle,
  IRaffleCategory,
  IRaffleInList,
  IResponseRaffleEntry,
  IUserDetails,
  UpdateRaffleDTO,
} from 'types/prize-draw/raffles'
import { IPrizeDrawFreeEntry, IPrizeDrawPrizePool } from 'types/prize-draw/prize-draw'
import { IPrizeDrawTicket } from 'types/prize-draw/ticket'
import { IVendorGamePayload } from 'types/prize-draw/vendor-game'
import { BASE_API_URL } from '@/configs'
import axios from 'axios'
import { getCookie, hasCookie } from 'cookies-next'
import { AuthBEAPIClient, BEAPIClient, handleResponseSuccess } from './BEClient'

const RAFFLE_ENDPOINTS = {
  GET_USER_DETAILS: () => `/games/raffles/users/me`,
  GET_PARTNER_RAFFLES: (partnerId: string | number) => `/games/partners/${partnerId}/raffles`,
  GET_RAFFLE: (raffleId: string | number) => `/games/raffles/${raffleId}`,
  UPDATE_RAFFLE: (raffleId: string | number) => `/games/raffles/${raffleId}`,
  GET_FREE_ENTRIES: (partnerId: string | number) => `/games/partners/${partnerId}/free-entries`,
  GET_PARTNER_RAFFLE_CATEGORIES: (partnerId: string | number) =>
    `/games/partners/${partnerId}/raffle-categories`,
  GET_RAFFLE_TICKETS: (raffleId: string | number) => `/games/raffles/${raffleId}/tickets`,
  GET_RAFFLE_PRIZES: (raffleId: string | number) => `/games/raffles/${raffleId}/prize-pools`,
  GET_GAME_PAYLOAD: (raffleId: string | number) => `/games/raffles/${raffleId}/game-payload`,
  POST_PURCHASE_TICKETS: (raffleId: string | number) => `/games/raffles/${raffleId}/purchase`,
  POST_CHECKOUT_INTENT: (raffleId: string | number) => `/games/raffles/${raffleId}/checkout-intent`,
  GET_CHECKOUT_ORDER: (orderId: number) => `/games/raffles/checkout-orders/${orderId}`,
  POST_RAFFLE_TICKET_REVEAL: (raffleId: number, ticketId: number) =>
    `/games/raffles/${raffleId}/tickets/${ticketId}/reveal`,
  POST_RAFFLE_TICKETS_REVEAL_ALL: (raffleId: number) =>
    `/games/raffles/${raffleId}/tickets/reveal-all`,
  GET_MY_ENTRIES: () => `/games/raffles/entries/me`,
}

export const raffleApi = {
  /**
   * GET /games/raffles/users/me
   * Fetch the current user's details.
   */
  getUserDetails: async () => {
    try {
      const response = await AuthBEAPIClient.get<IUserDetails>(RAFFLE_ENDPOINTS.GET_USER_DETAILS())
      return handleResponseSuccess(response)
    } catch (error) {
      throw new Error(`Failed to fetch user details: ${error}`)
    }
  },

  /**
   * GET /games/partners/{partner_id}/raffles
   * Fetch all raffles for a given partner, optionally filtered by status.
   * Pass `archive: true` to bypass the 24-hour cutoff on drawn draws (Previous Draw Results).
   */
  getPartnerRaffles: async (partnerId: string | number, params?: IGetPartnerRafflesParams) => {
    try {
      const response = await BEAPIClient.get<IRaffleInList[]>(
        RAFFLE_ENDPOINTS.GET_PARTNER_RAFFLES(partnerId),
        { params },
      )
      return handleResponseSuccess(response)
    } catch (error) {
      throw new Error(`Failed to fetch raffles for partner ${partnerId}: ${error}`)
    }
  },

  /**
   * GET /games/raffles/{raffle_id}
   * Fetch a single raffle by ID.
   */
  getRaffle: async (raffleId: string | number) => {
    try {
      const response = await BEAPIClient.get<IRaffle>(RAFFLE_ENDPOINTS.GET_RAFFLE(raffleId))
      return handleResponseSuccess(response)
    } catch (error) {
      throw new Error(`Failed to fetch raffle ${raffleId}: ${error}`)
    }
  },

  /**
   * GET /games/partners/{partner_id}/free-entries
   * Fetch all free entries for a given partner.
   */
  getFreeEntries: async (partnerId: string | number) => {
    try {
      const response = await AuthBEAPIClient.get<IPrizeDrawFreeEntry>(
        RAFFLE_ENDPOINTS.GET_FREE_ENTRIES(partnerId),
      )
      return handleResponseSuccess(response)
    } catch (error) {
      throw new Error(`Failed to fetch free entries: ${error}`)
    }
  },

  /**
   * GET /games/raffles/entries/me
   * Fetch all free entries for a given partner.
   */
  getFreeEntriesSSR: async (accessToken: string, raffleId: string | number) => {
    try {
      const response = await axios.get<IPrizeDrawFreeEntry>(
        `${BASE_API_URL}${RAFFLE_ENDPOINTS.GET_FREE_ENTRIES(raffleId)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      )

      return handleResponseSuccess(response)
    } catch (error) {
      throw new Error(`Failed to fetch tickets for raffle ${raffleId}: ${error}`)
    }
  },

  /**
   * PATCH /games/raffles/{raffle_id}
   * Update a raffle by ID. Requires authentication.
   */
  updateRaffle: async (raffleId: string | number, data: UpdateRaffleDTO) => {
    try {
      const response = await AuthBEAPIClient.patch<IRaffle>(
        RAFFLE_ENDPOINTS.UPDATE_RAFFLE(raffleId),
        data,
      )
      return handleResponseSuccess(response)
    } catch (error) {
      throw new Error(`Failed to update raffle ${raffleId}: ${error}`)
    }
  },

  /**
   * GET /games/partners/{partner_id}/raffles/categories
   * Fetch all raffles categories for a given partner.
   */
  getPartnerRaffleCategories: async (partnerId: string | number) => {
    try {
      const response = await BEAPIClient.get<IRaffleCategory[]>(
        RAFFLE_ENDPOINTS.GET_PARTNER_RAFFLE_CATEGORIES(partnerId),
      )
      return handleResponseSuccess(response)
    } catch (error) {
      throw new Error(`Failed to fetch raffles categories for partner ${partnerId}: ${error}`)
    }
  },

  /**
   * GET /games/raffles/{raffle_id}/prize-pools
   * Fetch all instant win prizes for a given raffle.
   */
  getRafflePrizes: async (raffleId: string | number) => {
    try {
      const response = await BEAPIClient.get<IPrizeDrawPrizePool[]>(
        RAFFLE_ENDPOINTS.GET_RAFFLE_PRIZES(raffleId),
      )
      return handleResponseSuccess(response)
    } catch (error) {
      throw new Error(`Failed to fetch prizes for raffle ${raffleId}: ${error}`)
    }
  },

  /**
   * GET /games/raffles/{raffle_id}/game-payload
   * Fetch the vendor game payload (tickets + prizes) for the current user in a raffle.
   * Only valid for raffles with game_type PENALTY_SHOOTOUT.
   */
  getGamePayload: async (raffleId: string | number) => {
    try {
      const response = await AuthBEAPIClient.get<IVendorGamePayload>(
        RAFFLE_ENDPOINTS.GET_GAME_PAYLOAD(raffleId),
      )
      return handleResponseSuccess(response)
    } catch (error) {
      throw new Error(`Failed to fetch game payload for raffle ${raffleId}: ${error}`)
    }
  },

  /**
   * POST /games/raffles/{raffle_id}/purchase
   * Buy tickets for a given raffle.
   */
  postPurchaseRaffleTickets: async (raffleId: string | number, data: IPrizeDrawPurchaseEntry) => {
    const response = await AuthBEAPIClient.post<IPrizeDrawPurchaseResponse>(
      RAFFLE_ENDPOINTS.POST_PURCHASE_TICKETS(raffleId),
      data,
    )
    return handleResponseSuccess(response)
  },

  /**
   * POST /games/raffles/{raffle_id}/checkout-intent
   * Start a "Deposit & Pay" checkout for the part of an order the wallet can't cover.
   */
  postCheckoutIntent: async (raffleId: string | number, data: ICheckoutIntentRequest) => {
    const response = await AuthBEAPIClient.post<ICheckoutIntentResponse>(
      RAFFLE_ENDPOINTS.POST_CHECKOUT_INTENT(raffleId),
      data,
    )
    return handleResponseSuccess(response)
  },

  /**
   * GET /games/raffles/checkout-orders/{order_id}
   * A "Deposit & Pay" order with its tickets and the wallet balance.
   */
  getCheckoutOrder: async (orderId: number) => {
    const response = await AuthBEAPIClient.get<ICheckoutOrder>(
      RAFFLE_ENDPOINTS.GET_CHECKOUT_ORDER(orderId),
    )
    return handleResponseSuccess(response)
  },

  /**
   * GET /games/raffles/{raffle_id}/tickets
   * Fetch all tickets for a given raffle.
   */
  getRaffleTickets: async (raffleId: string | number) => {
    try {
      const response = await AuthBEAPIClient.get<IPrizeDrawTicket[]>(
        RAFFLE_ENDPOINTS.GET_RAFFLE_TICKETS(raffleId),
      )
      return handleResponseSuccess(response)
    } catch (error) {
      throw new Error(`Failed to fetch tickets for raffle ${raffleId}: ${error}`)
    }
  },

  /**
   * GET /games/raffles/{raffle_id}/tickets
   * Fetch all tickets for a given raffle. (SSR)
   */
  getRaffleTicketsSSR: async (accessToken: string, raffleId: string | number) => {
    try {
      const response = await axios.get<IPrizeDrawTicket[]>(
        `${BASE_API_URL}${RAFFLE_ENDPOINTS.GET_RAFFLE_TICKETS(raffleId)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      )

      return handleResponseSuccess(response)
    } catch (error) {
      throw new Error(`Failed to fetch tickets for raffle ${raffleId}: ${error}`)
    }
  },

  /**
   * POST /games/raffles/{raffle_id}/tickets/{ticket_id}/reveal
   * Reveal a single ticket for a given raffle.
   */
  revealRaffleTicket: async (raffleId: number, ticketId: number) => {
    try {
      const response = await AuthBEAPIClient.post<IPrizeDrawTicket>(
        RAFFLE_ENDPOINTS.POST_RAFFLE_TICKET_REVEAL(raffleId, ticketId),
      )
      return handleResponseSuccess(response)
    } catch (error) {
      throw new Error(`Failed to reveal ticket ${ticketId} for raffle ${raffleId}: ${error}`)
    }
  },

  /**
   * POST /games/raffles/{raffle_id}/tickets/reveal-all
   * Reveal all tickets for a given raffle.
   */
  revealAllRaffleTickets: async (raffleId: number) => {
    try {
      const response = await AuthBEAPIClient.post<IPrizeDrawTicket[]>(
        RAFFLE_ENDPOINTS.POST_RAFFLE_TICKETS_REVEAL_ALL(raffleId),
      )
      return handleResponseSuccess(response)
    } catch (error) {
      throw new Error(`Failed to reveal all tickets for raffle ${raffleId}: ${error}`)
    }
  },

  revealAllRaffleTicketsOnUnload: (raffleId: number) => {
    if (!hasCookie('accessToken')) return
    const accessToken = getCookie('accessToken', {
      sameSite: process.env.NODE_ENV === 'production' ? 'none' : 'lax',
      secure: process.env.NODE_ENV === 'production',
    })
    fetch(`${BASE_API_URL}${RAFFLE_ENDPOINTS.POST_RAFFLE_TICKETS_REVEAL_ALL(raffleId)}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      keepalive: true,
    }).catch(() => {})
  },

  /**
   * GET /games/raffles/entries/me
   * Fetch a page of entries for the current user.
   */
  getMyEntries: async (params?: { limit?: number; before_id?: string }) => {
    try {
      const queryParams = new URLSearchParams()
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.before_id) queryParams.append('before_id', params.before_id)

      const queryString = queryParams.toString()
      const url = `${RAFFLE_ENDPOINTS.GET_MY_ENTRIES()}${queryString ? `?${queryString}` : ''}`

      const response = await AuthBEAPIClient.get<IResponseRaffleEntry[]>(url)
      return handleResponseSuccess(response)
    } catch (error) {
      throw new Error(`Failed to fetch entries for user: ${error}`)
    }
  },
}
